import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
  useTheme,
  useMediaQuery,
  Avatar,
  Divider,
  Snackbar,
  Alert,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Drawer,
  MenuItem,
  FormControl,
  Select,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import {
  DateRange as DateRangeIcon,
  AccessTime as TimeIcon,
  Person as PersonIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Print as PrintIcon,
  Usb as UsbIcon,
  CloudUpload as CloudUploadIcon,
  AutoAwesome as AutoAwesomeIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import auraBmpLogo from "../assets/AuraLogo.bmp";

// Enhanced Thermal Print Component with BMP Logo Support
const ThermalPrintDialog = ({
  open,
  onClose,
  appointment
}) => {
  const [isPrinting, setIsPrinting] = useState(false);
  const [printMethod, setPrintMethod] = useState('browser');
  const [status, setStatus] = useState('');

  // Use the directly imported BMP logo
  const logoSource = auraBmpLogo;

  // Safe patient name display
  const getPatientName = () => {
    if (!appointment) return 'N/A';

    const name = appointment.MAD_FULL_NAME ||
      appointment.patientName ||
      appointment.name ||
      'Unknown Patient';

    return String(name).trim() || 'Unknown Patient';
  };

  const getPatientNumber = () => {
    if (!appointment) return 'N/A';

    const patientNo = appointment.MAD_PATIENT_NO ||
      appointment.patientNo ||
      appointment.channelNumber ||
      'N/A';

    return String(patientNo).trim() || 'N/A';
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';

    try {
      return new Date(`1970-01-01T${timeString}`).toLocaleTimeString("en-LK", {
        hour: "numeric",
        minute: "numeric",
        hour12: true,
      });
    } catch (error) {
      console.error('Error formatting time:', error);
      return 'Invalid Time';
    }
  };

  // Generate ESC/POS commands for thermal printers
  const generateEscPosCommands = () => {
    const currentDate = new Date().toLocaleDateString('en-LK');
    const currentTime = new Date().toLocaleTimeString('en-LK', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });

    // ESC/POS commands
    const commands = [
      '\x1B\x40', // Initialize printer
      '\x1B\x61\x01', // Center alignment
      '\x1B\x21\x30', // Double height and width
      'AURA MEDITATION CENTER\n',
      '\x1B\x21\x00', // Normal text
      '\x1B\x61\x00', // Left alignment
      '================================\n',
      '\x1B\x61\x01', // Center alignment
      '\x1B\x45\x01', // Bold on
      'APPOINTMENT CONFIRMATION\n',
      '\x1B\x45\x00', // Bold off
      '================================\n',
      '\x1B\x61\x01', // Center alignment
      '\x1B\x45\x01', // Bold on
      `${getPatientName()}\n`,
      '\x1B\x45\x00', // Bold off
      '================================\n',
      '\x1B\x61\x01', // Center alignment
      '\x1B\x21\x30', // Double height and width
      'APPOINMENT NUMBER\n',
      `${getPatientNumber()}\n`,
      '\x1B\x21\x00', // Normal text
      '\n',
      '================================\n',
      '\x1B\x61\x01', // Center alignment
      '\x1B\x21\x38', // Triple height and width - LARGEST SIZE
      '\x1B\x45\x01', // Bold on
      'MAXIMUM TIME 30 MINUTES \n',
      '\x1B\x45\x00', // Bold off
      '\x1B\x21\x00', // Normal text - Back to normal size
      '\n\n\n\n',
      '\x1D\x56\x41\x03' // Cut paper
    ];

    return commands.join('');
  };

  // Method 1: Browser Print with BMP Logo and Thermal Printer CSS
  const handleBrowserPrint = () => {
    setIsPrinting(true);
    setStatus('Preparing print with BMP logo...');

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Appointment Receipt - Aura Meditation Center</title>
        <style>
          @media print {
            @page {
              margin: 0;
              size: 80mm 150mm;
              margin: 2mm;
            }
            
            body {
              margin: 0;
              padding: 2mm;
              font-family: 'Courier New', monospace;
              font-size: 10px;
              line-height: 1.2;
              width: 76mm;
              background: white;
              color: black;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            
            .receipt {
              width: 76mm;
              margin: 0 auto;
            }
            
            .header {
              text-align: center;
              margin-bottom: 3px;
            }
            
            .center {
              text-align: center;
            }
            
            .bold {
              font-weight: bold;
            }
            
            .large {
              font-size: 14px;
              font-weight: bold;
            }
            
            .x-large {
              font-size: 16px;
              font-weight: bold;
            }
            
            .xx-large {
              font-size: 18px;
              font-weight: bold;
            }
            
            .divider {
              border-bottom: 2px solid #000;
              margin: 3px 0;
              height: 2px;
            }
            
            .double-divider {
              border-bottom: 3px solid #000;
              margin: 4px 0;
              height: 3px;
            }
            
            .channel-number {
              text-align: center;
              margin: 8px 0;
              padding: 5px;
              border: 3px solid #000;
              background: #f0f0f0;
            }
            
            .maximum-time {
              text-align: center;
              font-weight: bold;
              font-size: 18px;
              margin: 8px 0;
              padding: 4px;
              background: #000 !important;
              color: #fff !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
              border: 2px solid #000;
            }
            
            .footer {
              text-align: center;
              margin-top: 10px;
              font-weight: bold;
            }
            
            .logo {
              text-align: center;
              margin-bottom: 5px;
            }
            
            .patient-name {
              text-align: center;
              font-size: 18px;
              font-weight: bold;
              margin: 5px 0;
            }
            
            .timestamp {
              text-align: center;
              font-weight: bold;
              color: #000 !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
              margin-top: 5px;
              font-size: 9px;
            }
            
            /* Enhanced BMP logo styling for dark backgrounds */
            .bmp-logo {
              max-width: 35mm;
              height: auto;
              display: block;
              margin: 0 auto 3px auto;
              filter: invert(1) brightness(2) contrast(1000%) saturate(0%);
              -webkit-filter: invert(1) brightness(2) contrast(1000%) saturate(0%);
              image-rendering: pixelated;
              image-rendering: -webkit-optimize-contrast;
            }
            
            /* Alternative filter for better visibility */
            .bmp-logo-alt {
              max-width: 35mm;
              height: auto;
              display: block;
              margin: 0 auto 3px auto;
              filter: brightness(0) invert(1) contrast(1000%);
              -webkit-filter: brightness(0) invert(1) contrast(1000%);
            }
            
            /* Simple black and white conversion */
            .bmp-logo-simple {
              max-width: 35mm;
              height: auto;
              display: block;
              margin: 0 auto 3px auto;
              filter: grayscale(100%) contrast(1000%) brightness(0%);
              -webkit-filter: grayscale(100%) contrast(1000%) brightness(0%);
            }
            
            .text-left {
              text-align: left;
            }
            
            .text-right {
              text-align: right;
            }
            
            .row {
              display: flex;
              justify-content: space-between;
              margin: 2px 0;
              border-bottom: 1px solid #333;
              padding-bottom: 1px;
            }

            .header .x-large {
              text-shadow: 0.5px 0.5px 0.5px #000;
            }
            
            .channel-number .x-large {
              text-shadow: 0.5px 0.5px 0.5px #000;
            }
            
            .receipt {
              box-shadow: 0 0 0 1px #000;
            }
          }
          
          @media screen {
            body {
              background: #f5f5f5;
              padding: 20px;
            }
            .receipt {
              border: 2px dashed #666;
              background: white;
              padding: 10px;
            }
          }
        </style>
      </head>
      <body>
        <div class="receipt">
          <!-- BMP Logo Section with multiple filter options -->
          <div class="logo">
            <img src="${logoSource}" alt="Aura Meditation Center" class="bmp-logo">
          </div>
          
          <div class="header">
            <div class="x-large">AURA MEDITATION CENTER</div>
            <div class="double-divider"></div>
            <div class="large">APPOINTMENT CONFIRMATION</div>
            <div class="double-divider"></div>
          </div>
          
          <!-- Centered Patient Name -->
          <div class="patient-name">
            ${getPatientName()}
          </div>
          
          <div class="divider"></div>
          
          <div class="channel-number">
            <div class="x-large">APPOINMENT NUMBER</div>
            <div class="x-large">${getPatientNumber()}</div>
          </div>
          
          <div class="divider"></div>
          
          <!-- Enhanced Maximum Time Section -->
          <div class="maximum-time">
            MAXIMUM TIME 30 MINUTES
          </div>
          
          <div class="footer">
            <div class="bold">THANK YOU!</div>
            <div class="bold">Aura Meditation Center</div>
          </div>
          
          <div class="divider"></div>
          
          <!-- Darker Timestamp -->
          <div class="timestamp">
            <strong>Generated: ${new Date().toLocaleString('en-LK')}</strong>
          </div>
        </div>
        
        <script>
          window.onload = function() {
            setTimeout(function() {
              window.print();
              setTimeout(function() {
                window.close();
              }, 500);
            }, 500);
          };
          
          setTimeout(function() {
            if (!document.hidden) {
              window.print();
              setTimeout(function() {
                window.close();
              }, 500);
            }
          }, 3000);
        </script>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank', 'width=400,height=600');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      setStatus('Print dialog opened with BMP logo');
    } else {
      setStatus('Failed to open print window. Please allow popups.');
    }

    setTimeout(() => {
      setIsPrinting(false);
      onClose();
    }, 2000);
  };

  // Method 2: Download ESC/POS file for manual transfer
  const handleDownloadEscPos = () => {
    setIsPrinting(true);
    setStatus('Generating ESC/POS file...');

    try {
      const escPosData = generateEscPosCommands();

      const blob = new Blob([escPosData], { type: 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `appointment-${getPatientNumber()}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setStatus('ESC/POS file downloaded. Transfer to printer manually.');
      setTimeout(() => {
        setIsPrinting(false);
        onClose();
      }, 2000);
    } catch (error) {
      setStatus('Error generating file: ' + error.message);
      setIsPrinting(false);
    }
  };

  // Method 3: Server-side printing
  const handleServerPrint = async () => {
    setIsPrinting(true);
    setStatus('Sending to server printer...');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/print/appointment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          appointment,
          type: 'thermal',
          timestamp: new Date().toISOString()
        }),
      });

      if (response.ok) {
        const result = await response.json();
        setStatus('Print job sent to server successfully!');
        setTimeout(() => {
          setIsPrinting(false);
          onClose();
        }, 1500);
      } else {
        throw new Error('Server returned error: ' + response.status);
      }
    } catch (error) {
      setStatus('Server printing failed: ' + error.message);
      setTimeout(() => {
        setStatus('Falling back to browser print...');
        handleBrowserPrint();
      }, 1500);
    }
  };

  // Method 4: Image-based print with BMP logo
  const handleImagePrint = () => {
    setIsPrinting(true);
    setStatus('Generating printable image with BMP logo...');

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Print Receipt</title>
        <style>
          @page { margin: 0; size: 80mm auto; }
          body { 
            margin: 2mm; 
            font-family: Arial, sans-serif;
            font-size: 9px;
            line-height: 1.1;
            width: 76mm;
          }
          .container { 
            border: 1px solid #000; 
            padding: 3mm;
            text-align: center;
          }
          .logo-container {
            text-align: center;
            margin-bottom: 2mm;
          }
          .bmp-logo {
            max-width: 30mm;
            height: auto;
            filter: invert(1) brightness(2) contrast(1000%) saturate(0%);
            -webkit-filter: invert(1) brightness(2) contrast(1000%) saturate(0%);
          }
          .header { font-size: 11px; font-weight: bold; margin-bottom: 2mm; }
          .channel { font-size: 14px; font-weight: bold; margin: 3mm 0; border: 2px solid #000; padding: 2mm; }
          .maximum-time { 
            background: #000 !important; 
            color: #fff !important; 
            padding: 3mm; 
            margin: 3mm 0; 
            font-weight: bold;
            text-align: center;
            font-size: 16px;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            border: 2px solid #000;
          }
          .footer { margin-top: 3mm; font-weight: bold; }
          .patient-name {
            text-align: center;
            font-size: 16px;
            font-weight: bold;
            margin: 3mm 0;
          }
          .timestamp {
            text-align: center;
            font-weight: bold;
            color: #000 !important;
            margin-top: 2mm;
            font-size: 8px;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="logo-container">
            <img src="${logoSource}" alt="Aura Logo" class="bmp-logo">
          </div>
          <div class="header">AURA MEDITATION CENTER<br>APPOINTMENT CONFIRMATION</div>
          <div style="border-bottom: 1px solid #000; margin: 2mm 0;"></div>
          
          <!-- Centered Patient Name -->
          <div class="patient-name">${getPatientName()}</div>
          
          <div style="text-align: left;">
            <div><strong>Time:</strong> ${formatTime(appointment?.MAD_ALLOCATED_TIME)}</div>
            <div><strong>Contact:</strong> ${appointment?.MAD_CONTACT || 'N/A'}</div>
            <div><strong>Date:</strong> ${new Date().toLocaleDateString('en-LK')}</div>
          </div>
          <div style="border-bottom: 1px solid #000; margin: 2mm 0;"></div>
          <div class="channel">APPOINMENT NUMBER<br>${getPatientNumber()}</div>
          <div style="border-bottom: 1px solid #000; margin: 2mm 0;"></div>
          
          <!-- Enhanced Maximum Time Section -->
          <div class="maximum-time">MAXIMUM TIME 30 MINUTES</div>
          
          <div class="footer">THANK YOU!<br>Aura Meditation Center</div>
          
          <!-- Darker Timestamp -->
          <div class="timestamp"><strong>Printed: ${new Date().toLocaleString('en-LK')}</strong></div>
        </div>
        <script>
          setTimeout(() => window.print(), 500);
          setTimeout(() => window.close(), 2000);
        </script>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
    }

    setTimeout(() => {
      setIsPrinting(false);
      onClose();
    }, 3000);
  };

  const handlePrint = () => {
    switch (printMethod) {
      case 'browser':
        handleBrowserPrint();
        break;
      case 'server':
        handleServerPrint();
        break;
      case 'download':
        handleDownloadEscPos();
        break;
      case 'image':
        handleImagePrint();
        break;
      default:
        handleBrowserPrint();
    }
  };

  const printMethods = [
    {
      id: 'browser',
      name: 'Browser Thermal Print',
      description: 'Optimized for thermal printers with BMP logo',
      icon: <PrintIcon />,
    },
    {
      id: 'image',
      name: 'Simple Print with Logo',
      description: 'Basic printable format with BMP logo',
      icon: <PrintIcon />,
    },
    {
      id: 'download',
      name: 'Download ESC/POS File',
      description: 'Download file for manual transfer to thermal printer',
      icon: <DownloadIcon />,
    },
    {
      id: 'server',
      name: 'Server Print',
      description: 'Send to network/shared printer',
      icon: <CloudUploadIcon />,
    }
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Print Appointment Receipt
        <Chip label="BMP Logo" color="success" size="small" sx={{ ml: 1 }} />
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Select printing method for thermal receipt with BMP logo support
        </Typography>

        <FormControl fullWidth sx={{ mb: 3 }}>
          <Select
            value={printMethod}
            onChange={(e) => setPrintMethod(e.target.value)}
            disabled={isPrinting}
          >
            {printMethods.map(method => (
              <MenuItem key={method.id} value={method.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {method.icon}
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      {method.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {method.description}
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Enhanced Print Preview with BMP Logo */}
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Receipt Preview with BMP Logo
            </Typography>
            <Paper
              sx={{
                p: 2,
                backgroundColor: '#f5f5f5',
                fontFamily: 'Courier New, monospace',
                fontSize: '10px',
                lineHeight: '1.2',
                border: '1px dashed #ccc'
              }}
            >
              {/* BMP Logo Preview with enhanced visibility */}
              <Box sx={{ textAlign: 'center', mb: 1 }}>
                <img
                  src={logoSource}
                  alt="Aura Logo"
                  style={{
                    maxWidth: '35mm',
                    height: 'auto',
                    filter: 'invert(1) brightness(2) contrast(1000%) saturate(0%)'
                  }}
                />
              </Box>

              <div style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '12px' }}>
                AURA MEDITATION CENTER
              </div>
              <div style={{ borderBottom: '1px solid #000', margin: '3px 0' }}></div>
              <div style={{ textAlign: 'center', fontWeight: 'bold' }}>APPOINTMENT CONFIRMATION</div>
              <div style={{ borderBottom: '1px solid #000', margin: '3px 0' }}></div>

              {/* Centered Patient Name */}
              <div style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '14px', margin: '8px 0' }}>
                {getPatientName()}
              </div>

              <div style={{ borderBottom: '1px solid #000', margin: '3px 0' }}></div>
              <div style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '14px', margin: '5px 0' }}>
                APPOINMENT NUMBER<br />
                {getPatientNumber()}
              </div>
              <div style={{ borderBottom: '1px solid #000', margin: '3px 0' }}></div>

              {/* Enhanced Maximum Time Section */}
              <div style={{
                textAlign: 'center',
                fontWeight: 'bold',
                fontSize: '16px',
                margin: '8px 0',
                padding: '4px',
                backgroundColor: '#000',
                color: '#fff',
                border: '2px solid #000'
              }}>
                MAXIMUM TIME 30 MINUTES
              </div>

              <div style={{ textAlign: 'center', fontWeight: 'bold' }}>
                THANK YOU!<br />Aura Meditation Center
              </div>

              {/* Darker Timestamp */}
              <div style={{ textAlign: 'center', marginTop: '8px', fontWeight: 'bold', color: '#000', fontSize: '9px' }}>
                Generated: {new Date().toLocaleString('en-LK')}
              </div>
            </Paper>

            {/* Filter options for testing */}
            <Box sx={{ mt: 2, p: 1, backgroundColor: '#f8f8f8', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                <strong>Logo Visibility Options:</strong> If the logo appears too dark, try different filter combinations:
              </Typography>
              <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="caption">
                  • <strong>Current:</strong> invert(1) brightness(2) contrast(1000%)
                </Typography>
                <Typography variant="caption">
                  • <strong>Alternative 1:</strong> brightness(0) invert(1) contrast(1000%)
                </Typography>
                <Typography variant="caption">
                  • <strong>Alternative 2:</strong> grayscale(100%) contrast(1000%) brightness(0%)
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {status && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {status}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isPrinting}>
          Cancel
        </Button>
        <Button
          onClick={handlePrint}
          variant="contained"
          disabled={isPrinting}
          startIcon={isPrinting ? <CircularProgress size={16} /> : <PrintIcon />}
        >
          {isPrinting ? 'Printing...' : 'Print Receipt'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Universal Printing Component (Simplified)
const UniversalPrintDialog = ({
  open,
  onClose,
  appointment,
  onThermalPrint
}) => {
  const [isPrinting, setIsPrinting] = useState(false);

  const handleQuickPrint = () => {
    onClose();
    onThermalPrint();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Print Options
        <Chip label="BMP Logo" color="success" size="small" sx={{ ml: 1 }} />
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Choose how to print the appointment receipt with BMP logo support
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Card
              variant="outlined"
              sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
              onClick={handleQuickPrint}
            >
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <PrintIcon color="primary" />
                  <Box>
                    <Typography variant="h6">
                      Thermal Receipt Print
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Optimized for thermal printers with BMP logo and multiple format options
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Enhanced AppointmentsModal with better patient name handling
const AppointmentsModal = ({
  open,
  onClose,
  appointments,
  searchTerm,
  onSearchChange,
  isLoading,
  onStatusUpdate,
  onAddRecord,
  onCancelAppointment,
  onPrintAppointment,
  selectedSlotId,
  onRefresh,
  viewMode
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Safe patient name extraction
  const getPatientName = (appointment) => {
    if (!appointment) return 'Unknown Patient';

    const name = appointment.MAD_FULL_NAME ||
      appointment.patientName ||
      appointment.name ||
      'Unknown Patient';

    return String(name).trim() || 'Unknown Patient';
  };

  const getPatientInitial = (appointment) => {
    const name = getPatientName(appointment);
    return name.charAt(0).toUpperCase();
  };

  const filteredAppointments = appointments.filter((appointment) =>
    getPatientName(appointment).toLowerCase().includes(searchTerm.toLowerCase()) ||
    (appointment.MAD_CONTACT && appointment.MAD_CONTACT.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';

    try {
      return new Date(`1970-01-01T${timeString}`).toLocaleTimeString("en-LK", {
        hour: "numeric",
        minute: "numeric",
        hour12: true,
      });
    } catch (error) {
      console.error('Error formatting time:', error);
      return 'Invalid Time';
    }
  };

  // Mobile drawer content
  const mobileContent = (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" fontWeight="bold" color="primary">
          Appointments
        </Typography>
        <Box>
          <IconButton onClick={onRefresh} size="small" color="primary">
            <RefreshIcon />
          </IconButton>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      <TextField
        size="small"
        fullWidth
        placeholder="Search patients..."
        value={searchTerm}
        onChange={onSearchChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="primary" />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : filteredAppointments.length === 0 ? (
        <Typography variant="body1" color="textSecondary" sx={{ textAlign: "center", py: 4 }}>
          {searchTerm ? 'No patients found matching your search' : 'No appointments found for this timeslot'}
        </Typography>
      ) : (
        <Box sx={{ maxHeight: '70vh', overflow: 'auto' }}>
          {filteredAppointments.map((appointment) => (
            <Card
              key={appointment.MAD_APPOINMENT_ID}
              sx={{
                mb: 2,
                opacity: appointment.MAD_STATUS === "I" ? 0.6 : 1,
                backgroundColor: appointment.MAD_STATUS === "I" ? '#ffe6e6' : 'inherit'
              }}
            >
              <CardContent sx={{ p: 2 }}>
                {/* Patient Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ width: 40, height: 40, mr: 2 }}>
                    {getPatientInitial(appointment)}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {getPatientName(appointment)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {appointment.MAD_CONTACT || 'No contact info'}
                    </Typography>
                  </Box>
                  <Chip
                    label={appointment.MAD_PATIENT_NO || 'N/A'}
                    color="primary"
                    size="small"
                  />
                </Box>

                <Divider sx={{ my: 1 }} />

                {/* Appointment Details */}
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">Time:</Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {formatTime(appointment.MAD_ALLOCATED_TIME)}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">Status:</Typography>
                    {appointment.MAD_STATUS === "I" ? (
                      <Chip label="Cancelled" color="error" size="small" />
                    ) : appointment.MAD_STATUS === "c" ? (
                      <Chip
                        icon={<CheckCircleIcon fontSize="small" />}
                        label="Completed"
                        color="success"
                        size="small"
                      />
                    ) : appointment.TreatmentStatus === "C" ? (
                      <Chip
                        icon={<CheckCircleIcon fontSize="small" />}
                        label="Completed"
                        color="success"
                        size="small"
                      />
                    ) : appointment.TreatmentStatus === "P" ? (
                      <Chip
                        icon={<CheckCircleIcon fontSize="small" />}
                        label="Preparation Complete"
                        color="info"
                        size="small"
                        clickable
                        onClick={() => onStatusUpdate(appointment)}
                      />
                    ) : (
                      <Chip
                        icon={<PendingIcon fontSize="small" />}
                        label="Pending"
                        color="warning"
                        size="small"
                        clickable
                        onClick={() => onStatusUpdate(appointment)}
                      />
                    )}
                  </Box>
                </Box>

                <Divider sx={{ my: 1 }} />

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    fullWidth
                    startIcon={<AddIcon />}
                    onClick={() => {
                      onAddRecord(
                        appointment.MAD_PATIENT_CODE,
                        appointment.MAD_APPOINMENT_ID,
                        appointment.MAD_PATIENT_NO
                      );
                      onClose();
                    }}
                    disabled={appointment.MAD_STATUS === "I" || appointment.TreatmentStatus === "C"}
                  >
                    Add Treatment
                  </Button>

                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    fullWidth
                    startIcon={<CloseIcon />}
                    onClick={() => onCancelAppointment(appointment.MAD_APPOINMENT_ID)}
                    disabled={appointment.MAD_STATUS === "I" || appointment.TreatmentStatus === "C"}
                  >
                    Cancel Appointment
                  </Button>

                  <Button
                    variant="contained"
                    size="small"
                    color="secondary"
                    fullWidth
                    startIcon={<PrintIcon />}
                    onClick={() => onPrintAppointment(appointment)}
                  >
                    Print Receipt
                  </Button>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  );

  // Desktop table content
  const desktopContent = (
    <>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" component="h2" fontWeight="bold" color="primary">
            Appointments ({filteredAppointments.length})
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Button
              startIcon={<RefreshIcon />}
              onClick={onRefresh}
              size="small"
              variant="outlined"
            >
              Refresh
            </Button>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ mb: 2 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search patients by name or contact..."
            value={searchTerm}
            onChange={onSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ color: "primary.main" }}>
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredAppointments.length === 0 ? (
          <Typography variant="body1" color="textSecondary" sx={{ textAlign: "center", py: 4 }}>
            {searchTerm ? 'No patients found matching your search' : 'No appointments found for this timeslot'}
          </Typography>
        ) : (
          <TableContainer>
            <Table size="medium">
              <TableHead sx={{ backgroundColor: theme.palette.primary.light }}>
                <TableRow>
                  {/* Conditionally render table headers based on view mode */}
                  {viewMode === 'history' ? (
                    <>
                      <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>No.</TableCell>
                      <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>Code</TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>Patient</TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>Contact</TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>Patient</TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>Contact</TableCell>
                      <TableCell sx={{ color: "white", fontWeight: "bold" }}>Time</TableCell>
                      <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>No.</TableCell>
                      {/* <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>Code</TableCell> */}
                      <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>Status</TableCell>
                      <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>Actions</TableCell>
                    </>
                  )}
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredAppointments.map((appointment) => (
                  <TableRow
                    key={appointment.MAD_APPOINMENT_ID}
                    hover
                    sx={{
                      opacity: appointment.MAD_STATUS === "I" ? 0.6 : 1,
                      backgroundColor: appointment.MAD_STATUS === "I" ? '#ffe6e6' : 'inherit'
                    }}
                  >
                    {/* Conditionally render table cells based on view mode */}
                    {viewMode === 'history' ? (
                      <>
                        <TableCell align="center">
                          <Chip
                            label={appointment.MAD_PATIENT_NO || 'N/A'}
                            color="primary"
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          {appointment.MAD_PATIENT_CODE || 'N/A'}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Avatar sx={{ width: 32, height: 32, mr: 1 }}>
                              {getPatientInitial(appointment)}
                            </Avatar>
                            {getPatientName(appointment)}
                          </Box>
                        </TableCell>
                        <TableCell>{appointment.MAD_CONTACT || 'N/A'}</TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Avatar sx={{ width: 32, height: 32, mr: 1 }}>
                              {getPatientInitial(appointment)}
                            </Avatar>
                            {getPatientName(appointment)}
                          </Box>
                        </TableCell>
                        <TableCell>{appointment.MAD_CONTACT || 'N/A'}</TableCell>
                        <TableCell>
                          {formatTime(appointment.MAD_ALLOCATED_TIME)}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={appointment.MAD_PATIENT_NO || 'N/A'}
                            color="primary"
                            size="small"
                          />
                        </TableCell>
                        {/* <TableCell align="center">
                        {appointment.MAD_PATIENT_CODE || 'N/A'}
                      </TableCell> */}
                        <TableCell align="center">
                          {appointment.MAD_STATUS === "I" ? (
                            <Chip label="Cancelled" color="error" size="small" />
                          ) : appointment.MAD_STATUS === "c" ? (
                            <Chip
                              icon={<CheckCircleIcon fontSize="small" />}
                              label="Completed"
                              color="success"
                              size="small"
                            />
                          ) : appointment.TreatmentStatus === "C" ? (
                            <Chip
                              icon={<CheckCircleIcon fontSize="small" />}
                              label="Completed"
                              color="success"
                              size="small"
                            />
                          ) : appointment.TreatmentStatus === "P" ? (
                            <Chip
                              icon={<CheckCircleIcon fontSize="small" />}
                              label="Prep Complete"
                              color="info"
                              size="small"
                              clickable
                              onClick={() => onStatusUpdate(appointment)}
                            />
                          ) : (
                            <Chip
                              icon={<PendingIcon fontSize="small" />}
                              label="Pending"
                              color="warning"
                              size="small"
                              clickable
                              onClick={() => onStatusUpdate(appointment)}
                            />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: "flex", flexDirection: isSmallMobile ? "column" : "row", gap: 1, justifyContent: "center" }}>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<AddIcon />}
                              onClick={() => {
                                onAddRecord(
                                  appointment.MAD_PATIENT_CODE,
                                  appointment.MAD_APPOINMENT_ID,
                                  appointment.MAD_PATIENT_NO
                                );
                                onClose();
                              }}
                              disabled={appointment.MAD_STATUS === "I" || appointment.TreatmentStatus === "C"}
                              sx={{ minWidth: isSmallMobile ? "100%" : "auto" }}
                            >
                              {isSmallMobile ? "Add" : "Add Treatment"}
                            </Button>

                            <Button
                              variant="outlined"
                              size="small"
                              color="error"
                              startIcon={<CloseIcon />}
                              onClick={() => onCancelAppointment(appointment.MAD_APPOINMENT_ID)}
                              disabled={appointment.MAD_STATUS === "I" || appointment.TreatmentStatus === "C"}
                              sx={{ minWidth: isSmallMobile ? "100%" : "auto" }}
                            >
                              {isSmallMobile ? "Cancel" : "Cancel"}
                            </Button>

                            <Button
                              variant="contained"
                              size="small"
                              color="secondary"
                              startIcon={<PrintIcon />}
                              onClick={() => onPrintAppointment(appointment)}
                              sx={{ minWidth: isSmallMobile ? "100%" : "auto" }}
                            >
                              {isSmallMobile ? "Print" : "Print"}
                            </Button>
                          </Box>
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </>
  );
  // const desktopContent = (
  //   <>
  //     <DialogTitle>
  //       <Box display="flex" justifyContent="space-between" alignItems="center">
  //         <Typography variant="h5" component="h2" fontWeight="bold" color="primary">
  //           Appointments ({filteredAppointments.length})
  //         </Typography>
  //         <Box display="flex" alignItems="center" gap={1}>
  //           <Button
  //             startIcon={<RefreshIcon />}
  //             onClick={onRefresh}
  //             size="small"
  //             variant="outlined"
  //           >
  //             Refresh
  //           </Button>
  //           <IconButton onClick={onClose}>
  //             <CloseIcon />
  //           </IconButton>
  //         </Box>
  //       </Box>
  //     </DialogTitle>

  //     <DialogContent dividers>
  //       <Box sx={{ mb: 2 }}>
  //         <TextField
  //           size="small"
  //           fullWidth
  //           placeholder="Search patients by name or contact..."
  //           value={searchTerm}
  //           onChange={onSearchChange}
  //           InputProps={{
  //             startAdornment: (
  //               <InputAdornment position="start" sx={{ color: "primary.main" }}>
  //                 <SearchIcon />
  //               </InputAdornment>
  //             ),
  //           }}
  //         />
  //       </Box>

  //       {isLoading ? (
  //         <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
  //           <CircularProgress />
  //         </Box>
  //       ) : filteredAppointments.length === 0 ? (
  //         <Typography variant="body1" color="textSecondary" sx={{ textAlign: "center", py: 4 }}>
  //           {searchTerm ? 'No patients found matching your search' : 'No appointments found for this timeslot'}
  //         </Typography>
  //       ) : (
  //         <TableContainer>
  //           <Table size="medium">
  //             <TableHead sx={{ backgroundColor: theme.palette.primary.light }}>
  //               <TableRow>
  //                 <TableCell sx={{ color: "white", fontWeight: "bold" }}>Patient</TableCell>
  //                 <TableCell sx={{ color: "white", fontWeight: "bold" }}>Contact</TableCell>
  //                 <TableCell sx={{ color: "white", fontWeight: "bold" }}>Time</TableCell>
  //                 <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>No.</TableCell>
  //                 <TableCell align="center" sx={{ color: "white", fontWeight: "bold" }}>Code</TableCell>
  //               </TableRow>
  //             </TableHead>

  //             <TableBody>
  //               {filteredAppointments.map((appointment) => (
  //                 <TableRow
  //                   key={appointment.MAD_APPOINMENT_ID}
  //                   hover
  //                   sx={{
  //                     opacity: appointment.MAD_STATUS === "I" ? 0.6 : 1,
  //                     backgroundColor: appointment.MAD_STATUS === "I" ? '#ffe6e6' : 'inherit'
  //                   }}
  //                 >
  //                   <TableCell>
  //                     <Box sx={{ display: "flex", alignItems: "center" }}>
  //                       <Avatar sx={{ width: 32, height: 32, mr: 1 }}>
  //                         {getPatientInitial(appointment)}
  //                       </Avatar>
  //                       {getPatientName(appointment)}
  //                     </Box>
  //                   </TableCell>
  //                   <TableCell>{appointment.MAD_CONTACT || 'N/A'}</TableCell>
  //                   <TableCell>
  //                     {formatTime(appointment.MAD_ALLOCATED_TIME)}
  //                   </TableCell>
  //                   <TableCell align="center">
  //                     <Chip
  //                       label={appointment.MAD_PATIENT_NO || 'N/A'}
  //                       color="primary"
  //                       size="small"
  //                     />
  //                   </TableCell>

  //                   <TableCell align="center">
  //                     {appointment.MAD_PATIENT_CODE || 'N/A'}
  //                   </TableCell>
  //                 </TableRow>
  //               ))}
  //             </TableBody>
  //           </Table>
  //         </TableContainer>
  //       )}
  //     </DialogContent>
  //   </>
  // );

  if (isMobile) {
    return (
      <Drawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        sx={{
          '& .MuiDrawer-paper': {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            maxHeight: '90vh',
          },
        }}
      >
        {mobileContent}
      </Drawer>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
    >
      {desktopContent}
    </Dialog>
  );
};

// Main Component
export default function DailyAppointment() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  const [timeslots, setTimeslots] = useState([]);
  const [error, setError] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [historyAppointments, setHistoryAppointments] = useState([]);
  const [selectedHistoryAppointment, setSelectedHistoryAppointment] = useState(null);
  const [selectedSlotId, setSelectedSlotId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState('daily'); // 'daily' or 'history'
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [universalPrintDialogOpen, setUniversalPrintDialogOpen] = useState(false);
  const [thermalPrintDialogOpen, setThermalPrintDialogOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [selectedDate, setSelectedDate] = useState(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });

  const dateInputRef = useRef(null);

  const showSnackbar = (message, severity = "info") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  useEffect(() => {
    // Fetch data based on current view mode when component first loads
    if (viewMode === 'daily') {
      fetchTimeslotsByDate(selectedDate);
    } else if (viewMode === 'history') {
      fetchHistoryAppointments(selectedDate);
    }
  }, []);

  // Auto-refresh appointments every 1 minute when modal is open
  useEffect(() => {
    let intervalId;

    if (isModalOpen && selectedSlotId) {
      // Refresh immediately when modal opens
      handleRefreshAppointments();

      // Set up interval for auto-refresh every 1 minute
      intervalId = setInterval(() => {
        handleRefreshAppointments();
      }, 60000); // 60 seconds = 1 minute

      showSnackbar("Auto-refresh enabled - updating every minute", "info");
    }

    // Cleanup interval on modal close or component unmount
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
        console.log("Auto-refresh interval cleared");
      }
    };
  }, [isModalOpen, selectedSlotId]);

  const fetchTimeslotsByDate = async (dateParam) => {
    const date = dateParam || selectedDate;
    setIsLoading(true);
    try {
      // Backend path changed to `active-timeslots-bydates` and new host
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/Timeslot/active-timeslots-bydates?date=${date}`
      );
      setTimeslots(response.data);
      setError(null);
    } catch (error) {
      setTimeslots([]);
      setError("No timeslots available for the selected date.");
      console.error("Error fetching timeslots:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // NOTE: Do not auto-fetch when the user changes the date.
  // Fetching will be performed manually by the user via the "Refresh" button.

  // Fetch history appointments for the selected date
  const fetchHistoryAppointments = async (dateParam) => {
    const date = dateParam || selectedDate;
    if (!date) return;
    setIsHistoryLoading(true);
    try {
      const base = process.env.REACT_APP_API_BASE_URL;
      // Only call the Timeslot history endpoint (path param)
      const response = await axios.get(`${base}/Timeslot/timeslothistory/${date}`);

      if (response && response.data) {
        // Keep timeslot items (each has Timeslot and Appointments[])
        setHistoryAppointments(response.data);
        // count total appointments
        const total = (response.data || []).reduce((sum, it) => sum + ((it.Appointments && it.Appointments.length) || 0), 0);
        showSnackbar(`Loaded ${total} history records`, 'success');
      } else {
        setHistoryAppointments([]);
        showSnackbar('No history appointments found for selected date', 'info');
      }
    } catch (error) {
      console.error('Error fetching history appointments:', error);
      setHistoryAppointments([]);
      showSnackbar('History endpoint not available - contact admin', 'warning');
    } finally {
      setIsHistoryLoading(false);
    }
  };

  const handleViewAppointments = async (slotId) => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/Appointment/appointments/${slotId}`
      );
      setAppointments(response.data);
      setSelectedSlotId(slotId);
      setIsModalOpen(true);
      showSnackbar(`Loaded ${response.data.length} appointments`, "success");
    } catch (error) {
      console.error("Error fetching appointments:", error);
      showSnackbar("No appointments available for this timeslot", "info");
      setAppointments([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Manual refresh function
  const handleRefreshAppointments = async () => {
    if (!selectedSlotId) return;

    try {
      console.log("Refreshing appointments...");
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/Appointment/appointments/${selectedSlotId}`
      );
      setAppointments(response.data);
      console.log(`Refreshed ${response.data.length} appointments`);
    } catch (error) {
      console.error("Error refreshing appointments:", error);
      showSnackbar("Failed to refresh appointments", "error");
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedSlotId(null);
    setSearchTerm("");
  };

  const handleStatusUpdate = async (appointment) => {
    try {
      const patientCode = appointment.MAD_PATIENT_CODE;
      const appointmentID = appointment.MAD_APPOINMENT_ID;
      const phoneNumber = appointment.MAD_CONTACT; // Get phone number from appointment

      console.log('Updating status with:', { patientCode, appointmentID, phoneNumber });

      // First update the appointment status
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/Appointment/status/${appointmentID}`,
        JSON.stringify("c"),
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        console.log('Status updated successfully');

        // Send SMS after status is updated to complete
        if (phoneNumber) {
          try {
            const smsResponse = await axios.post(
              `${process.env.REACT_APP_API_BASE_URL}/Treatment/send-sms`,
              {
                Phone: phoneNumber
              },
              {
                headers: {
                  "Content-Type": "application/json",
                },
              }
            );

            if (smsResponse.status === 200) {
              console.log('SMS sent successfully');
              showSnackbar("Status updated to completed and SMS sent", "success");
            }
          } catch (smsError) {
            console.error('Error sending SMS:', smsError);
            showSnackbar("Status updated but failed to send SMS", "warning");
          }
        } else {
          showSnackbar("Status updated to completed (no phone number for SMS)", "success");
        }

        // Refresh the appointments data
        handleRefreshAppointments();
      }
    } catch (error) {
      console.error('Error updating status:', error);
      showSnackbar("Failed to update status", "error");
    }
  };

  const handleAddRecord = (patientId, appointmentID, patientNo) => {
    navigate(`/dashboard/addrecord/${patientId}`, {
      state: {
        appointmentid: appointmentID,
        channelnumber: patientNo,
        MTD_APPOINMENT_ID: appointmentID
      },
    });
  };

  const handlePrintAppointment = (appointment) => {
    setSelectedAppointment(appointment);
    setUniversalPrintDialogOpen(true);
  };

  const handleThermalPrint = () => {
    setThermalPrintDialogOpen(true);
  };

  const handleDateChange = (e) => {
    const newDate = e.target.value;
    setSelectedDate(newDate);
    setAppointments([]);
    setSelectedSlotId(null);

    // Auto-fetch for the currently selected viewMode
    if (viewMode === 'daily') {
      fetchTimeslotsByDate(newDate);
    } else if (viewMode === 'history') {
      fetchHistoryAppointments(newDate);
    }
  };

  // Manual loader: call the appropriate fetch depending on viewMode
  const handleFetchByDate = async () => {
    if (viewMode === 'daily') {
      await fetchTimeslotsByDate();
    } else if (viewMode === 'history') {
      await fetchHistoryAppointments();
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';

    try {
      return new Date(`1970-01-01T${timeString}`).toLocaleTimeString("en-LK", {
        hour: "numeric",
        minute: "numeric",
        hour12: true,
      });
    } catch (error) {
      console.error('Error formatting time:', error);
      return 'Invalid Time';
    }
  };

  const handleCancelAppointment = async (appointmentId) => {
    if (!window.confirm('Are you sure you want to cancel this appointment?')) {
      return;
    }

    try {
      setIsLoading(true);
      await axios.delete(
        `${process.env.REACT_APP_API_BASE_URL}/Appointment/cancel-appointment/${appointmentId}`
      );

      setAppointments(appointments.map(appt =>
        appt.MAD_APPOINMENT_ID === appointmentId
          ? { ...appt, MAD_STATUS: "I" }
          : appt
      ));

      showSnackbar("Appointment cancelled successfully", "success");
    } catch (error) {
      console.error("Error cancelling appointment:", error);
      showSnackbar("Failed to cancel appointment", "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Top-of-page toggle */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2, pl: { xs: 2, sm: 4 } }}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(e, val) => {
            if (!val) return;
            // Reset date to today when switching views and auto-fetch
            const now = new Date();
            const year = now.getFullYear();
            const month = String(now.getMonth() + 1).padStart(2, '0');
            const day = String(now.getDate()).padStart(2, '0');
            const today = `${year}-${month}-${day}`;

            setViewMode(val);
            setSelectedDate(today);

            if (val === 'history') {
              fetchHistoryAppointments(today);
            } else {
              fetchTimeslotsByDate(today);
            }

            // focus the date input if available
            if (dateInputRef && dateInputRef.current) dateInputRef.current.focus();
          }}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              color: theme.palette.primary.main,
              borderColor: theme.palette.primary.main,
            },
            '& .MuiToggleButton-root.Mui-selected': {
              backgroundColor: theme.palette.primary.main,
              color: '#fff',
            }
          }}
        >
          <ToggleButton value="daily">Daily Appointments</ToggleButton>
          <ToggleButton value="history">History Appointments</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box
          sx={{
            mb: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            color="primary"
            fontWeight={600}
            align="center"
          >
            {viewMode === 'daily' ? 'Daily Appointments' : 'History Appointments'}
          </Typography>



          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              gap: 2,
              mb: 3,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <TextField
              type="date"
              label="Select Date"
              value={selectedDate}
              onChange={handleDateChange}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DateRangeIcon sx={{ color: "primary.main" }} />
                  </InputAdornment>
                ),
              }}
              inputProps={{
                placeholder: "YYYY/MM/DD"
              }}
              inputRef={dateInputRef}
              sx={{
                minWidth: isMobile ? "100%" : 250,
                '& .MuiInputBase-input': {
                  position: 'relative',
                  backgroundColor: 'transparent',
                },
                '& input[type="date"]': {
                  color: 'transparent',
                  position: 'relative',
                  '&::-webkit-calendar-picker-indicator': {
                    opacity: 0,
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: '100%',
                    height: '100%',
                    cursor: 'pointer',
                    zIndex: 2,
                  },
                },
                '& .MuiInputBase-root': {
                  position: 'relative',
                  '&::after': {
                    content: selectedDate
                      ? `"${selectedDate.split('-').join('/')}"`
                      : '"YYYY/MM/DD"',
                    position: 'absolute',
                    left: '40px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: selectedDate ? 'rgba(0, 0, 0, 0.87)' : 'rgba(0, 0, 0, 0.6)',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    pointerEvents: 'none',
                    zIndex: 1,
                  }
                }
              }}
            />

          </Box>
        </Box>

        {viewMode === 'daily' ? (
          (isLoading && !selectedSlotId) ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Paper elevation={3} sx={{ p: 3, textAlign: "center" }}>
              <Typography variant="h6" color="textSecondary">
                {error}
              </Typography>
            </Paper>
          ) : (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography
                  variant="h6"
                  gutterBottom
                  color="primary"
                  fontWeight={600}
                >
                  Available Timeslots ({timeslots.length})
                </Typography>
                <Grid container spacing={2}>
                  {timeslots.map((timeslot) => (
                    <Grid item xs={12} key={timeslot.MT_SLOT_ID}>
                      <Card
                        elevation={3}
                        sx={{
                          borderLeft:
                            selectedSlotId === timeslot.MT_SLOT_ID
                              ? `4px solid ${theme.palette.primary.main}`
                              : "none",
                          cursor: "pointer",
                          "&:hover": {
                            boxShadow: 4,
                          },
                        }}
                        onClick={() =>
                          handleViewAppointments(timeslot.MT_SLOT_ID)
                        }
                      >
                        <CardContent>
                          <Box
                            sx={{ display: "flex", alignItems: "center", mb: 1 }}
                          >
                            <Avatar
                              sx={{ bgcolor: theme.palette.primary.main, mr: 2 }}
                            >
                              <PersonIcon />
                            </Avatar>
                            <Typography variant="subtitle1">
                              {timeslot.MT_DOCTOR || 'Unknown Doctor'}
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <TimeIcon color="action" sx={{ mr: 1 }} />
                            <Typography>
                              {formatTime(timeslot.MT_START_TIME)} -{" "}
                              {formatTime(timeslot.MT_END_TIME)}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          )
        ) : (
          isHistoryLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : historyAppointments.length === 0 ? (
            <Paper elevation={3} sx={{ p: 3, textAlign: "center" }}>
              <Typography variant="h6" color="textSecondary">
                {`No history appointments found for ${selectedDate}`}
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Button variant="outlined" onClick={fetchHistoryAppointments} startIcon={<RefreshIcon />}>Refresh</Button>
              </Box>
            </Paper>
          ) : (
            <Grid container spacing={3}>
              {historyAppointments.map((item, idx) => {
                const timeslot = item.Timeslot || {};
                const appts = item.Appointments || [];
                return (
                  <Grid item xs={12} key={timeslot.MT_SLOT_ID || idx}>
                    <Card
                      elevation={3}
                      sx={{ cursor: 'pointer' }}
                    >
                      <CardContent
                        sx={{
                          display: 'flex',
                          flexDirection: isMobile ? 'column' : 'row',
                          alignItems: isMobile ? 'flex-start' : 'center',
                          justifyContent: 'space-between',
                          gap: 2,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: isMobile ? '100%' : 'auto' }}>
                          <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                            <PersonIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle1" fontWeight="bold">{timeslot.MT_DOCTOR || 'Unknown Doctor'}</Typography>
                            <Typography variant="body2" color="text.secondary">{formatTime(timeslot.MT_START_TIME)} - {formatTime(timeslot.MT_END_TIME)}</Typography>
                          </Box>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: isMobile ? '100%' : 'auto', justifyContent: isMobile ? 'space-between' : 'flex-end' }}>
                          <Chip label={`${appts.length}`} color="primary" sx={{ ml: isMobile ? 0 : 1 }} />
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              setAppointments(appts);
                              setSelectedSlotId(timeslot.MT_SLOT_ID);
                              setIsModalOpen(true);
                            }}
                            sx={{
                              width: isMobile ? '48%' : 'auto',
                              ml: isMobile ? 0 : 0,
                            }}
                          >
                            View Appointments
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )
        )}

        {/* Responsive Appointments Modal */}
        <AppointmentsModal
          open={isModalOpen}
          onClose={handleCloseModal}
          appointments={appointments}
          searchTerm={searchTerm}
          onSearchChange={(e) => setSearchTerm(e.target.value)}
          isLoading={isLoading}
          onStatusUpdate={handleStatusUpdate}
          onAddRecord={handleAddRecord}
          onCancelAppointment={handleCancelAppointment}
          onPrintAppointment={handlePrintAppointment}
          selectedSlotId={selectedSlotId}
          onRefresh={handleRefreshAppointments}
          viewMode={viewMode}
        />

        {/* Universal Printing Dialog */}
        <UniversalPrintDialog
          open={universalPrintDialogOpen}
          onClose={() => setUniversalPrintDialogOpen(false)}
          appointment={selectedAppointment}
          onThermalPrint={handleThermalPrint}
        />

        {/* Thermal Printing Dialog with BMP Support */}
        <ThermalPrintDialog
          open={thermalPrintDialogOpen}
          onClose={() => setThermalPrintDialogOpen(false)}
          appointment={selectedAppointment}
        />

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Paper>
    </Container>
  );
}